/**
 * ДЕЙСТВИЯ над комнатами — единственное место, где клиент что-то МЕНЯЕТ на сервере.
 *
 * Почему отдельный модуль, а не вызовы SDK прямо из компонентов:
 *  1. Каждая из этих операций может упереться в `M_LIMIT_EXCEEDED` — на нашем
 *     Synapse `rc_room_creation` и `rc_message` реально режут (замерено прошлым
 *     заходом: набор стенда шёл ~1.4 комнаты в минуту). Ретрай с уважением к
 *     `retry_after_ms` должен быть ОДИН и общий, а не переписанный в каждой кнопке.
 *  2. Сообщения об ошибках человеку нужны на русском и по делу («не хватает прав»),
 *     а не `M_FORBIDDEN`. Перевод тоже должен жить в одном месте.
 *  3. Компоненты остаются про UI, а не про Matrix.
 *
 * ГРАНИЦА ОТВЕТСТВЕННОСТИ: здесь нет ни одного вызова, меняющего состояние
 * ЧУЖИХ комнат сверх того, что запросил пользователь кнопкой. Ничего
 * «на всякий случай» и никаких фоновых операций.
 */
import {
  EventType,
  EventStatus,
  RelationType,
  type StateEvents,
  MsgType,
  Preset,
  Visibility,
  type MatrixClient,
  type MatrixEvent,
  type Room,
} from "matrix-js-sdk";
import { tf, tr } from "../i18n";
// RoomMessageEventContent живёт в подмодуле @types/events и НЕ реэкспортится из корня
// пакета (замерено 14.09: импорт из "matrix-js-sdk" даёт TS2614).
import type { RoomMessageEventContent } from "matrix-js-sdk/lib/@types/events.js";

// Имя тега избранного берём из rooms.ts, а не объявляем второй раз: чтение
// (isPinned) и запись (setFavourite) обязаны смотреть на ОДНУ строку, иначе
// «пометил, а звёздочка не появилась» станет вопросом опечатки.

import { FAVOURITE_TAG } from "./rooms";
import { replyTargetId, stripReplyFallback } from "./relations";
import { ensureJpegAvatar } from "./avatarImage";

export { withRateLimit };

/**
 * Ошибка Matrix в понятном человеку виде.
 *
 * НЕ прячем причину: если сервер отказал, пользователь должен увидеть, почему,
 * иначе кнопка выглядит сломанной. Технический errcode оставляем в скобках —
 * без него нельзя будет разобрать инцидент по скриншоту.
 */
export function humanError(e: unknown): string {
  const err = e as { errcode?: string; message?: string; data?: { error?: string } };
  const detail = err?.data?.error || err?.message || String(e);

  // СЕТЬ ОТВЕЧАЕТ НЕ errcode, А ТЕКСТОМ БРАУЗЕРА — и он утекал на экран.
  //
  // Антон, 22.09 14:34 (скрин кабинета на macOS): красная плашка
  // «fetch failed: Load failed». Это не ответ Matrix: у такой ошибки нет
  // ни errcode, ни тела — запрос не дошёл вовсе. «Load failed» —
  // дословное сообщение WebKit при оборванном fetch, в Chrome то же
  // состояние звучит как «Failed to fetch». До этой правки default-ветка
  // ниже выводила его человеку без изменений.
  //
  // Почему словами, а не техническим текстом: действие человека
  // здесь другое. На M_FORBIDDEN надо просить права, на обрыв сети —
  // проверить связь и повторить. «fetch failed» не подсказывает ни того,
  // ни другого, и читается как «приложение сломалось».
  //
  // Сообщение БЕЗ detail намеренно: там ровно та же английская
  // строка браузера, которую мы и убираем.
  if (!err?.errcode && isNetworkFailure(err?.message, e)) {
    return tr("matrix.errorNetwork");
  }

  switch (err?.errcode) {
    case "M_FORBIDDEN":
      return tf("matrix.errorForbidden", { detail });
    case "M_LIMIT_EXCEEDED":
      return tr("matrix.errorRateLimit");
    case "M_NOT_FOUND":
      return tf("matrix.errorNotFound", { detail });
    case "M_UNKNOWN":
      return detail;
    default:
      return err?.errcode ? `${detail} (${err.errcode})` : detail;
  }
}

/**
 * Оборванный запрос или ответ сервера?
 *
 * Смотрим на текст, потому что больше смотреть не на что: `TypeError`
 * от fetch не несёт ни кода, ни причины — это сознательное решение
 * стандарта (защита от сканирования сети через различение ошибок).
 *
 * Перечислены формулировки всех трёх движков: WebKit — «Load failed»,
 * Chromium — «Failed to fetch», Gecko — «NetworkError when attempting to
 * fetch resource». Плюс «fetch failed» от undici (Electron/Node-оболочка) —
 * именно она была на скрине Антона, и в ней как раз две строки
 * сразу: «fetch failed: Load failed».
 *
 * Подстроковое сравнение — хрупко, и это надо называть вслух: при
 * смене формулировки в движке мы снова увидим сырой текст. Зато
 * ошибка никогда не теряется: неузнанное уйдёт в default-ветку как
 * раньше, а не в тишину.
 */
function isNetworkFailure(message: string | undefined, raw: unknown): boolean {
  const text = `${message ?? ""} ${raw instanceof Error ? raw.name : ""}`.toLowerCase();
  if (!text.trim()) return false;
  return (
    text.includes("load failed") ||
    text.includes("failed to fetch") ||
    text.includes("fetch failed") ||
    text.includes("networkerror") ||
    text.includes("network request failed") ||
    text.includes("err_internet_disconnected") ||
    text.includes("err_network")
  );
}

/**
 * Один общий ретрай на 429.
 *
 * Ждём ровно столько, сколько велел сервер (`retry_after_ms`), но не дольше
 * 15 секунд за попытку и не больше трёх попыток: дольше — это уже не «кнопка
 * подтормозила», а зависший интерфейс, и честнее показать ошибку.
 */
async function withRateLimit<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  // last обязан быть инициализирован ДО цикла: если fn() бросит не-429 на
  // первой же попытке, мы выйдем через `throw e` внутри — но TS не может это
  // доказать, и без инициализации здесь теоретически летит `undefined`.
  let last: unknown = new Error("withRateLimit: attempts exhausted");
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      last = e;
      const err = e as { errcode?: string; data?: { retry_after_ms?: number } };
      if (err?.errcode !== "M_LIMIT_EXCEEDED") throw e;
      const waitMs = Math.min(err.data?.retry_after_ms ?? 1000, 15_000) + 200;
      await new Promise((r) => setTimeout(r, waitMs));
    }
  }
  throw last;
}

/** Принять приглашение. После joinRoom комната сама переедет из invites в joined. */
export async function acceptInvite(client: MatrixClient, roomId: string): Promise<void> {
  await withRateLimit(() => client.joinRoom(roomId));
}

/**
 * Отклонить приглашение = выйти из состояния invite.
 *
 * `forget` НЕ вызываем: он окончательно убирает комнату из истории аккаунта,
 * и повторное приглашение будет выглядеть как новая комната. Отклонение
 * должно быть обратимым (позвали ещё раз — снова видно), удаление — нет.
 */
export async function declineInvite(client: MatrixClient, roomId: string): Promise<void> {
  await withRateLimit(() => client.leave(roomId));
}

/** Пометить/снять избранное. Один вызов на оба направления, чтобы UI не ветвился. */
export async function setFavourite(
  client: MatrixClient,
  roomId: string,
  on: boolean,
): Promise<void> {
  await withRateLimit(() =>
    on
      ? // order обязателен по спеке для сортировки внутри тега; 0.5 — середина,
        // как ставит Element при простом «в избранное» без ручного порядка.
        client.setRoomTag(roomId, FAVOURITE_TAG, { order: 0.5 })
      : client.deleteRoomTag(roomId, FAVOURITE_TAG),
  );
}

/**
 * ЗВОНОК С УРОВНЯ 0 — для каждой НОВОЙ комнаты (Антон, 2026-09-11 13:18).
 *
 * Зачем. `m.call.member` — state-событие, а порог на state-события в комнате
 * по умолчанию 50, а у рядового участника уровень 0. Замер на стенде
 * 11.09: участник входил в LiveKit и публиковал звук, но его членство в комнату
 * НЕ записывалось — и каждый сидел в звонке ОДИН, причём без ошибки на экране.
 *
 * Почему именно так, а не «выдать людям 50»: 50 — это порог СРАЗУ на всё
 * служебное, включая `redact` (удаление ЧУЖИХ сообщений) и настройки комнаты.
 * Звонок не должен требовать модераторства (Антон 13:18–13:19), поэтому
 * опускаем планку ТОЛЬКО для события звонка, не трогая остальные права.
 *
 * `power_level_content_override` сливается с пресетом на сервере: задаём только
 * поддерево `events`, поэтому users/users_default/state_default остаются
 * штатными (создатель 100, остальные 0).
 *
 * Старые комнаты это НЕ починит — им нужен отдельный прогон по API
 * (`staging-dumps/patch_call_power_levels.py` у Momo).
 */
const CALL_FROM_ZERO = {
  events: {
    "org.matrix.msc3401.call.member": 0,
  },
} as const;

/**
 * Создать ПРОСТРАНСТВО (спейс).
 *
 * Пространство — это обычная комната с `creation_content.type = "m.space"`.
 * До этой функции создать пространство из клиента было НЕВОЗМОЖНО вообще:
 * рельс и вкладки только показывали уже существующие спейсы, а Антон 12.09
 * 11:14 назвал отсутствие управления блокером приёмки всего блока.
 *
 * Пресет — PrivateChat, видимость — Private, и публичной галочки НЕТ.
 * Причина: «публичное пространство» в Matrix означает «виден всему серверу»,
 * а не «виден сотрудникам ALG» (та же ловушка, что в модели доступа п.4
 * для restricted-комнат). Закрытое умолчание безопаснее открытого по ошибке.
 *
 * CALL_FROM_ZERO тоже передаём: внутри спейса могут появиться звонки
 * (групповой звонок пространства — отдельная фича), и чинить PL потом
 * руками по API не хочется — цена одной строки сейчас.
 */
export async function createSpace(
  client: MatrixClient,
  opts: { name: string; topic?: string; invite?: string[] },
): Promise<{ roomId: string; failed: { userId: string; reason: string }[] }> {
  const res = await withRateLimit(() =>
    client.createRoom({
      name: opts.name,
      topic: opts.topic || undefined,
      preset: Preset.PrivateChat,
      visibility: Visibility.Private,
      creation_content: { type: "m.space" },
      power_level_content_override: CALL_FROM_ZERO,
    }),
  );

  // ПРИГЛАШЕНИЯ ОТДЕЛЬНЫМИ ВЫЗОВАМИ, А НЕ ПОЛЕМ `invite` В createRoom
  // (Антон 12.09 11:55: «need to be able to add members straight away»).
  // Причина: при списке в createRoom один плохой MXID роняет ВЕСЬ запрос —
  // пространство не создаётся вовсе, и человек теряет уже введённые имя и
  // тему. Здесь пространство уже существует, а каждое приглашение
  // независимо: кого смогли — позвали, по остальным вернули причину.
  // Последовательно, без Promise.all: сервер отвечает 429 на пачку.
  const failed: { userId: string; reason: string }[] = [];
  for (const userId of opts.invite ?? []) {
    try {
      await inviteUser(client, res.room_id, userId);
    } catch (e) {
      failed.push({ userId, reason: humanError(e) });
    }
  }
  return { roomId: res.room_id, failed };
}

/**
 * Отвязать комнату от пространства: снять m.space.child в спейсе и
 * встречный m.space.parent в комнате.
 *
 * Снятие child в Matrix — это отправка state-события с ПУСТЫМ content
 * (не redact: redact ломал бы историю аудита, а пустой content — штатный
 * способ «связь удалена», его же читает spaceMembership в rooms.ts:
 * `Object.keys(ev.getContent()).length > 0`).
 *
 * m.space.parent снимаем ВСЕГДА, но ошибку глотаем по той же причине,
 * что и в linkRoomToSpace: parent вспомогателен, дерево строится по child.
 * Отсутствие прав в комнате не должно отменять уже случившуюся отвязку
 * в пространстве.
 */
export async function unlinkRoomFromSpace(
  client: MatrixClient,
  roomId: string,
  spaceId: string,
): Promise<void> {
  const sendState = client.sendStateEvent.bind(client) as unknown as (
    roomId: string,
    eventType: string,
    content: Record<string, unknown>,
    stateKey: string,
  ) => Promise<unknown>;

  await withRateLimit(() => sendState(spaceId, "m.space.child", {}, roomId));
  try {
    await withRateLimit(() => sendState(roomId, "m.space.parent", {}, spaceId));
  } catch {
    // См. комментарий выше: parent — вспомогательное событие.
  }
}

/**
 * Перенести комнату между пространствами (или в/из «Дома»).
 *
 * fromSpaceId === null — комната была вне пространств (например, старая
 * `novaja`, которую Антон создавал «внутри ALG», а она оказалась ни в чём —
 * дефект 12.09 10:57). toSpaceId === null — вынуть из всех пространств
 * в «Дом». Оба null — ничего не делаем: вызывающий код не должен был
 * открыть такой диалог, но дешевле промолчать, чем ронять.
 *
 * Реализация — ПЕРЕИСПОЛЬЗОВАНИЕ linkRoomToSpace/unlinkRoomFromSpace,
 * а не своя отправка событий: оба шага уже умеют rate-limit, via и
 * правильную терпимость к ошибкам parent (требование постановки).
 *
 * ПРАВА проверяются СЕРВЕРОМ на каждом sendStateEvent: отправка
 * m.space.child требует power level в пространстве, и пользователь с PL 0
 * получит честный M_FORBIDDEN (модель доступа, п.3: список админов в
 * клиенте НЕ зашиваем — это была бы «защита», снимаемая правкой в браузере).
 * Ошибка первого же запрещённого шага всплывёт наверх через humanError.
 */
export async function moveRoomToSpace(
  client: MatrixClient,
  roomId: string,
  fromSpaceId: string | null,
  toSpaceId: string | null,
): Promise<void> {
  if (fromSpaceId === toSpaceId) return;
  if (fromSpaceId) {
    await unlinkRoomFromSpace(client, roomId, fromSpaceId);
  }
  if (toSpaceId) {
    await linkRoomToSpace(client, roomId, toSpaceId);
  }
}

/**
 * Есть ли у МЕНЯ права отправлять m.space.child в этом пространстве.
 *
 * Нужно ТОЛЬКО для того, чтобы не показывать пункт «Перенести в
 * пространство…» тому, кто заведомо получит отказ (модель доступа п.3/4d:
 * «клиент лишь не предлагает недопустимое»). Настоящая защита — серверная:
 * даже если пункт как-то показался, Synapse вернёт M_FORBIDDEN на отправке.
 *
 * Читаем ЛОКАЛЬНОЕ состояние (currentState.maySendStateEvent): отдельный
 * HTTP-запрос на каждое меню не нужен — power levels уже пришли со sync.
 */
export function mayManageSpaceChild(client: MatrixClient, spaceId: string): boolean {
  const me = client.getUserId();
  if (!me) return false;
  const space = client.getRoom(spaceId);
  if (!space) return false;
  return space.currentState.maySendStateEvent("m.space.child", me);
}

/**
 * Может ли я ПРИГЛАШАТЬ в это пространство.
 *
 * Отдельно от mayManageSpaceChild: приглашение — не state-событие, у него
 * свой порог `invite` в m.room.power_levels. Спрашиваем SDK напрямую,
 * порог не пересчитываем руками.
 */
export function mayInviteToSpace(client: MatrixClient, spaceId: string): boolean {
  const me = client.getUserId();
  if (!me) return false;
  const space = client.getRoom(spaceId);
  if (!space) return false;
  const levels = space.currentState.getStateEvents("m.room.power_levels", "");
  const needed = (levels?.getContent()?.invite as number | undefined) ?? 0;
  return (space.getMember(me)?.powerLevel ?? 0) >= needed;
}

/**
 * Могу ли я ПЕРЕИМЕНОВАТЬ пространство: право слать m.room.name.
 */
export function mayRenameSpace(client: MatrixClient, spaceId: string): boolean {
  const me = client.getUserId();
  if (!me) return false;
  const space = client.getRoom(spaceId);
  if (!space) return false;
  return space.currentState.maySendStateEvent("m.room.name", me);
}

/**
 * Может ли ЭТОТ пользователь создавать пространства.
 *
 * Антон 12.09 11:49: «available to admins only» — кнопка создания
 * пространства только у админов.
 *
 * ЧЕМ ОПРЕДЕЛЯЕТСЯ АДМИН. Списка MXID здесь нет и быть не должно (спека
 * п.3): хардкод снимается правкой в браузере за минуту. Признак —
 * ИЗМЕРЯЕМЫЙ: есть ли у меня уровень администратора (PL >= 100) хотя бы
 * в одном существующем пространстве. У Антона и Момо он есть, у рядового
 * участника PL 0 — нет.
 *
 * ЧЕСТНАЯ ГРАНИЦА ЭТОЙ ПРОВЕРКИ. Она КОСМЕТИЧЕСКАЯ. Matrix по умолчанию
 * разрешает создать комнату/пространство любому пользователю сервера, и
 * спрятанная кнопка этого не меняет: тот же человек создаст пространство
 * через Element или обычным HTTP-запросом. Настоящий запрет ставится
 * только на сервере (Synapse, ограничение создания комнат) — это НЕ
 * сделано и требует отдельной работы по серверу.
 */
export function mayCreateSpace(client: MatrixClient): boolean {
  const me = client.getUserId();
  if (!me) return false;
  return client
    .getRooms()
    .filter((r) => r.isSpaceRoom())
    .some((sp) => (sp.getMember(me)?.powerLevel ?? 0) >= 100);
}

/**
 * Создать комнату.
 *
 * Публичная = `preset: public_chat` + `visibility: public`. Второе — публикация
 * в каталоге сервера; без него «публичная» комната остаётся ненайденной, и
 * пользователь справедливо решит, что галочка ничего не делает.
 * Шифрование НЕ включаем: crypto в этом клиенте выключен намеренно, создать
 * зашифрованную комнату означало бы создать комнату, которую сами не прочитаем.
 */
export async function createRoom(
  client: MatrixClient,
  opts: { name: string; isPublic: boolean; topic?: string; parentSpaceId?: string | null },
): Promise<string> {
  const res = await withRateLimit(() =>
    client.createRoom({
      name: opts.name,
      topic: opts.topic || undefined,
      preset: opts.isPublic ? Preset.PublicChat : Preset.PrivateChat,
      visibility: opts.isPublic ? Visibility.Public : Visibility.Private,
      power_level_content_override: CALL_FROM_ZERO,
    }),
  );
  const roomId = res.room_id;

  // ПРИВЯЗКА К ПРОСТРАНСТВУ — ОТДЕЛЬНЫЙ ШАГ, И ЕГО РАНЬШЕ НЕ БЫЛО.
  //
  // Дефект, найденный по словам Антона 12.09 10:57: «комнату novaja я
  // создавал, находясь в пространстве ALG». Комната при этом оказалась ВНЕ
  // всех пространств. Причина: createRoom создавал комнату и на этом
  // заканчивал — в Matrix принадлежность пространству это НЕ свойство
  // комнаты, а отдельное событие состояния m.space.child В САМОМ
  // ПРОСТРАНСТВЕ. Никто его не отправлял, поэтому «создал внутри ALG»
  // было правдой только в интерфейсе.
  //
  // Последствие было шире косметики: по модели доступа (10:45-10:49)
  // комната вне пространств НЕ ограничена границей спейса. То есть каждая
  // созданная из интерфейса комната молча оказывалась вне правила
  // безопасности.
  if (opts.parentSpaceId) {
    await linkRoomToSpace(client, roomId, opts.parentSpaceId);
  }
  return roomId;
}

/**
 * Привязать комнату к пространству: m.space.child в пространстве плюс
 * встречный m.space.parent в комнате.
 *
 * Оба события нужны по разным причинам: child задаёт СОСТАВ пространства
 * (по нему строится дерево), parent позволяет комнате знать своего
 * родителя — без него комната не сможет впустить участников пространства
 * по restricted-правилу, когда мы до него дойдём.
 *
 * Отсутствие прав в пространстве НЕ роняет создание комнаты: комната уже
 * создана, и терять её из-за неудачной привязки нельзя. Ошибку возвращаем
 * наверх текстом, чтобы человек увидел «комната создана, но не привязана»,
 * а не молчаливое несоответствие.
 */
export async function linkRoomToSpace(
  client: MatrixClient,
  roomId: string,
  spaceId: string,
): Promise<void> {
  const via = [client.getDomain()].filter((d): d is string => Boolean(d));
  // sendStateEvent типизирован по известным SDK типам событий; m.space.child
  // и m.space.parent в этот union не входят. Приводим сам метод, а не
  // содержимое: так теряется ровно проверка имени события, а не структура.
  const sendState = client.sendStateEvent.bind(client) as unknown as (
    roomId: string,
    eventType: string,
    content: Record<string, unknown>,
    stateKey: string,
  ) => Promise<unknown>;

  await withRateLimit(() =>
    sendState(spaceId, "m.space.child", { via, suggested: false }, roomId),
  );
  try {
    await withRateLimit(() =>
      sendState(roomId, "m.space.parent", { via, canonical: true }, spaceId),
    );
  } catch {
    // m.space.parent требует прав в КОМНАТЕ и считается вспомогательным:
    // дерево пространства строится по child. Молча переживаем.
  }
}

/**
 * Создать личный чат (DM) с пользователем.
 *
 * ДВА шага, и второй так же обязателен, как первый: `is_direct: true` живёт
 * только в приглашении и в состоянии комнаты НЕ остаётся. Единственный
 * источник правды про «это личный чат» — account data `m.direct` (см. rooms.ts).
 * Без записи в него комната появится в общем списке, но НЕ во вкладке «Личные»,
 * и выглядеть это будет как баг фильтра.
 *
 * Существующий m.direct читаем и ДОПОЛНЯЕМ, а не перезаписываем: там лежат
 * все DM аккаунта, включая созданные в Element.
 */
export function findExistingDirectMessage(
  client: MatrixClient,
  userId: string,
): string | null {
  const direct =
    client.getAccountData(EventType.Direct)?.getContent<Record<string, string[]>>() ?? {};
  const listed = (direct[userId] ?? []).find((id) => {
    const room = client.getRoom(id);
    return room && room.getMyMembership() === "join";
  });
  if (listed) return listed;

  // m.direct — данные АККАУНТА, и они врут в обе стороны: комната, созданная
  // СОБЕСЕДНИКОМ, у нас в списке не числится, пока мы сами туда не запишем.
  // Именно так и появляются два чата с одним человеком — каждый создал свой.
  // Поэтому вторым шагом ищем ЛЮБУЮ комнату ровно на двоих с этим человеком.
  const me = client.getUserId();
  for (const room of client.getRooms()) {
    if (room.getMyMembership() !== "join") continue;
    const people = room
      .getMembers()
      .filter((m) => m.membership === "join" || m.membership === "invite")
      .map((m) => m.userId);
    if (people.length !== 2) continue;
    if (people.includes(userId) && me && people.includes(me)) return room.roomId;
  }
  return null;
}

/**
 * Создать личный чат — ИЛИ вернуть уже существующий.
 *
 * ЗАПРЕТ ДУБЛЕЙ (Антон, 2026-09-14 12:44): двух личных комнат с одним
 * человеком быть не может. Проверка стоит ЗДЕСЬ, а не в диалоге, чтобы её
 * нельзя было обойти новым местом вызова: у Антона в списке 14.09 было
 * ДВА чата с andrey именно потому, что диалог создания проверки не имел.
 *
 * Честная граница: это защита КЛИЕНТА. Дубль, созданный из Element или
 * с телефона, она не остановит — для этого нужен модуль на стороне Synapse.
 */
export async function createDirectMessage(
  client: MatrixClient,
  userId: string,
): Promise<string> {
  const existing = findExistingDirectMessage(client, userId);
  if (existing) {
    // Комната могла быть создана собеседником и у нас в m.direct не числиться —
    // дописываем, иначе она не попадёт во вкладку «Личные».
    await rememberDirect(client, userId, existing);
    return existing;
  }
  const res = await withRateLimit(() =>
    client.createRoom({
      preset: Preset.TrustedPrivateChat,
      invite: [userId],
      is_direct: true,
      power_level_content_override: CALL_FROM_ZERO,
    }),
  );
  const roomId = res.room_id;
  await rememberDirect(client, userId, roomId);
  return roomId;
}

/** Дописать комнату в m.direct, не теряя чужие записи и не плодя повторы. */
async function rememberDirect(
  client: MatrixClient,
  userId: string,
  roomId: string,
): Promise<void> {
  const current =
    client.getAccountData(EventType.Direct)?.getContent<Record<string, string[]>>() ?? {};
  const mine = current[userId] ?? [];
  if (mine.includes(roomId)) return;
  const next: Record<string, string[]> = { ...current };
  next[userId] = [...mine, roomId];
  await withRateLimit(() => client.setAccountData(EventType.Direct, next));
}

/** Пригласить пользователя в существующую комнату. */
/**
 * Существует ли пользователь. Проверяем ДО приглашения.
 *
 * Зачем: формат MXID можно набрать безупречно и всё равно попасть в пустоту —
 * `@momo:algsussecc.com` (Антон, 2026-09-10 11:15) выглядит законно, но такого
 * сервера нет. Синтаксическая проверка тут бессильна, нужен реальный запрос.
 *
 * Возвращает: null — пользователь есть; иначе человекочитаемая причина.
 * НИКОГДА не бросает: недоступность профиля не должна ронять диалог.
 */
export async function whyUserUnusable(
  client: MatrixClient,
  userId: string,
): Promise<string | null> {
  try {
    await client.getProfileInfo(userId);
    return null;
  } catch (e) {
    const err = e as { errcode?: string; message?: string; data?: { error?: string } };
    const detail = err?.data?.error || err?.message || "";
    if (err?.errcode === "M_NOT_FOUND") {
      return tf("matrix.errorUserNotFoundOnServer", { user: userId });
    }
    // Нет такого домена / DNS не отвечает / федерация закрыта. Текст сервера
    // тут самый полезный — он называет хост.
    if (/connect|federat|dns|timeout|unreachable|not known/i.test(detail)) {
      return tf("matrix.errorServerUnreachable", { detail });
    }
    // Профиль скрыт настройками приватности — пользователь может существовать.
    // Блокировать приглашение из-за этого нельзя.
    return null;
  }
}

export async function inviteUser(
  client: MatrixClient,
  roomId: string,
  userId: string,
): Promise<void> {
  await withRateLimit(() => client.invite(roomId, userId));
}

/**
 * Выйти из комнаты.
 *
 * `forget` сознательно НЕ вызываем по той же причине, что и при отклонении
 * приглашения: выход должен быть обратимым, а «забыть» — отдельное решение.
 */
export async function leaveRoom(client: MatrixClient, roomId: string): Promise<void> {
  await withRateLimit(() => client.leave(roomId));
}

/**
 * Почему НЕЛЬЗЯ выйти из пространства. null = выходить можно.
 *
 * Правило Антона 12.09 11:51: «leave space should be forbidden for the admin
 * until there are users in this space and no other admin» — последний админ
 * не может уйти, пока в пространстве остались люди.
 *
 * ЗАЧЕМ. Пространство без единого админа становится неуправляемым НАВСЕГДА:
 * поднять кому-то права может только тот, у кого права уже есть. Оставшиеся
 * участники не смогут ни пригласить нового человека, ни добавить комнату,
 * ни переименовать — и починить это изнутри Matrix нельзя, только через
 * администратора сервера. Это ловушка без выхода, поэтому ставим запрет
 * заранее, а не объясняем потом.
 *
 * ТРИ УСЛОВИЯ ОДНОВРЕМЕННО (иначе выход разрешён):
 *   1. я админ (PL >= 100);
 *   2. других админов нет;
 *   3. в пространстве остались другие люди.
 * Если я единственный участник — выход СВОБОДЕН: бросать пустое
 * пространство некому, запрет был бы бессмысленным.
 *
 * ЧЕСТНАЯ ГРАНИЦА: проверка клиентская. Сервер такой запрет не ставит, и
 * тот же человек уйдёт через Element. Это защита от случайности, а не от
 * намерения.
 */
export function whyCannotLeaveSpace(client: MatrixClient, spaceId: string): string | null {
  const me = client.getUserId();
  if (!me) return null;
  const space = client.getRoom(spaceId);
  if (!space) return null;

  const joined = space.getMembersWithMembership("join" as never);
  const myLevel = space.getMember(me)?.powerLevel ?? 0;
  if (myLevel < 100) return null; // не админ — уходит свободно

  const others = joined.filter((m) => m.userId !== me);
  if (others.length === 0) return null; // один в пространстве — уходить можно

  const otherAdmins = others.filter((m) => (m.powerLevel ?? 0) >= 100);
  if (otherAdmins.length > 0) return null; // есть кому управлять

  return (
    tf("space.lastAdminHead", { count: others.length }) +
    `${others.length === 1 ? tr("ui.uchastnik") : tr("ui.uchastnikov")}. ` +
    tr("ui.bez_administratora_prostranstvom_nelzya") +
    tr("ui.ni_dobavit_komnatu_ni_izmenit_nastroyk") +
    tr("ui.administratora_ili_udalite_ostalnyh_ucha")
  );
}

export interface MemberInfo {
  userId: string;
  name: string;
  initials: string;
  avatarUrl: string | null;
  membership: "join" | "invite";
  /** Уровень прав; 100 — админ, 50 — модератор. Показываем только заметные. */
  powerLevel: number;
}

/**
 * Список участников комнаты.
 *
 * Источник — локальное состояние комнаты (`room.getMembers()`), а не
 * HTTP `/joined_members`: состояние уже пришло со sync, лишний запрос на
 * каждое открытие панели не нужен, и приглашённые (`invite`) в нём тоже есть,
 * а в `/joined_members` их по определению нет.
 *
 * ОГРАНИЧЕНИЕ, которое надо знать: при `lazy_load_members` (мы его не включаем,
 * но сервер может отдать неполный state) список будет неполным. Проверить это
 * можно сравнением с `getJoinedMemberCount()` — расхождение показываем в UI,
 * а не молча рисуем короткий список.
 */
export function membersOf(client: MatrixClient, room: Room): MemberInfo[] {
  // client больше не нужен для аватара (отдаём mxc://, качает useAuthedAvatar),
  // но остаётся в сигнатуре: его передают все вызывающие места, и менять их
  // ради одного аргумента дороже, чем оставить стабильный интерфейс.
  void client;
  const pl = room.currentState
    .getStateEvents(EventType.RoomPowerLevels, "")
    ?.getContent<{ users?: Record<string, number>; users_default?: number }>();
  const users = pl?.users ?? {};
  const dflt = pl?.users_default ?? 0;

  return room
    .getMembers()
    .filter((m) => m.membership === "join" || m.membership === "invite")
    .map((m) => ({
      userId: m.userId,
      name: m.name || m.userId,
      initials: initialsOfMember(m.name || m.userId),
      avatarUrl: m.getMxcAvatarUrl() ?? null,
      membership: (m.membership === "invite" ? "invite" : "join") as "join" | "invite",
      powerLevel: users[m.userId] ?? dflt,
    }))
    // Сначала по правам (админы сверху), потом по алфавиту: так список
    // читается как структура комнаты, а не как случайный набор.
    .sort((a, b) => b.powerLevel - a.powerLevel || a.name.localeCompare(b.name, "ru"));
}

function initialsOfMember(name: string): string {
  const clean = name.replace(/^@/, "");
  const words = clean.split(/[\s_\-.]+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Кикнуть участника.
 *
 * Причина опциональна, но просим в UI: «кик без объяснения» вызывает
 * больше вопросов, чем решает. Сервер причину не проверяет — она идёт
 * в событие m.room.member и видна другим клиентам.
 */
export async function kickUser(
  client: MatrixClient,
  roomId: string,
  userId: string,
  reason?: string,
): Promise<void> {
  await withRateLimit(() => client.kick(roomId, userId, reason));
}

/**
 * УДАЛИТЬ ЧЕЛОВЕКА ИЗ ПРОСТРАНСТВА — правило Антона 12.09 11:56:
 * «removing members automatically disconnects them from all the rooms,
 * but not deleting any rooms. Rooms with only 2 participants will remain
 * with 1 participant and full history.»
 *
 * ЧТО ДЕЛАЕМ: кик из самого пространства + кик из КАЖДОЙ его комнаты, где
 * человек состоит. Иначе удаление было бы фикцией: членство в спейсе он
 * теряет, а в комнатах внутри продолжает читать и писать.
 *
 * ЧЕГО НЕ ДЕЛАЕМ, СОЗНАТЕЛЬНО:
 *  - не удаляем комнаты, даже если в них остался один человек. Комната с
 *    одним участником и полной историей — нормальное состояние Matrix,
 *    её содержимое принадлежит и тому, кто остался;
 *  - не чистим историю: сообщения ушедшего остаются, как и в любом
 *    мессенджере. Кик не переписывает прошлое;
 *  - не баним. Кик обратим: человека можно позвать обратно.
 *
 * Идём последовательно и НЕ падаем на первой ошибке: нет прав в одной
 * комнате — остальные всё равно нужно закрыть. Список неудач возвращаем
 * наверх, чтобы интерфейс назвал их поимённо, а не соврал «готово».
 */
export async function removeFromSpace(
  client: MatrixClient,
  spaceId: string,
  userId: string,
  childRoomIds: string[],
  reason?: string,
): Promise<{ failed: { roomId: string; roomName: string; reason: string }[] }> {
  const failed: { roomId: string; roomName: string; reason: string }[] = [];

  for (const roomId of childRoomIds) {
    const room = client.getRoom(roomId);
    if (!room) continue;
    const m = room.getMember(userId);
    // Кикаем и приглашённых: неотозванное приглашение — это вход в комнату.
    if (!m || (m.membership !== "join" && m.membership !== "invite")) continue;
    try {
      await kickUser(client, roomId, userId, reason);
    } catch (e) {
      failed.push({ roomId, roomName: room.name || roomId, reason: humanError(e) });
    }
  }

  // Пространство — ПОСЛЕДНИМ. Если начать с него, при ошибке в комнатах
  // человек уже выпал из спейса, и найти его оставшиеся комнаты в
  // интерфейсе будет нечем.
  const space = client.getRoom(spaceId);
  try {
    await kickUser(client, spaceId, userId, reason);
  } catch (e) {
    failed.push({
      roomId: spaceId,
      roomName: space?.name || spaceId,
      reason: humanError(e),
    });
  }

  return { failed };
}

/**
 * Забанить пользователя.
 *
 * Бан — это membership "ban": человек не может войти, пока его не разбанят,
 * даже по новому приглашению. Поэтому бан строже кика, и в UI у него
 * отдельное подтверждение с текстом про необратимость входа.
 */
export async function banUser(
  client: MatrixClient,
  roomId: string,
  userId: string,
  reason?: string,
): Promise<void> {
  await withRateLimit(() => client.ban(roomId, userId, reason));
}

/**
 * Снять бан. Возвращает человека в membership "leave": после разбана он
 * НЕ оказывается в комнате автоматически — ему надо заново войти (или его
 * приглашают). UI обязан это сказать, иначе «разбанил, а его нет» — баг.
 */
export async function unbanUser(
  client: MatrixClient,
  roomId: string,
  userId: string,
): Promise<void> {
  await withRateLimit(() => client.unban(roomId, userId));
}

/** Текущее содержимое m.room.power_levels в сыром виде (или undefined). */
export function readPowerLevels(room: Room): Record<string, unknown> | undefined {
  return room.currentState
    .getStateEvents(EventType.RoomPowerLevels, "")
    ?.getContent() as Record<string, unknown> | undefined;
}

/**
 * Изменить power level ОДНОГО пользователя.
 *
 * ПРАВИЛА БЕЗОПАСНОСТИ (их нарушение ломает комнату для всех — читать до конца):
 *
 * 1. Content отправляется ЦЕЛИКОМ. Читаем текущий state, меняем в нём ОДНО
 *    поле users[userId], отправляем обратно всё, включая незнакомые нам ключи
 *    (notifications, invite, redact и т.д.). Написать content с нуля — значит
 *    молча сбросить все остальные настройки комнаты на дефолты спеки.
 *
 * 2. Удаление из users (level === users_default) делаем через delete, а не
 *    через запись дефолтного числа: иначе map раздувается мёртвыми записями,
 *    и при users_default=50 запись «user:50» НЕ равна отсутствию записи
 *    для серверной логики наследования.
 *
 * 3. Свой собственный PL понижать через этот вызов нельзя — иначе возможна
 *    ситуация «понизил себя, теперь не могу вернуть». Понижение себя —
 *    отдельное осознанное действие, здесь оно запрещено.
 */
export async function setPowerLevel(
  client: MatrixClient,
  room: Room,
  userId: string,
  level: number,
): Promise<void> {
  const me = client.getUserId();
  if (!me) throw new Error(tr("ui.net_userid_klienta"));
  if (userId === me) {
    throw new Error(tr("ui.izmenenie_sobstvennogo_urovnya_prav_zdes"));
  }

  const current = readPowerLevels(room) ?? {};
  const users = { ...((current.users as Record<string, number> | undefined) ?? {}) };
  const usersDefault =
    typeof current.users_default === "number" ? current.users_default : 0;

  if (level === usersDefault) {
    delete users[userId];
  } else {
    users[userId] = level;
  }

  // Разворачиваем current первым: все незнакомые ключи выживают. users —
  // единственное поле, которое мы реально меняем.
  const next = { ...current, users };

  await withRateLimit(() =>
    client.sendStateEvent(room.roomId, EventType.RoomPowerLevels, next),
  );
}

/** Может ли текущий пользователь приглашать в эту комнату (гасим кнопку, а не ловим 403). */
export function canInvite(client: MatrixClient, room: Room): boolean {
  const me = client.getUserId();
  if (!me) return false;
  return room.canInvite(me);
}

/**
 * Сегмент сообщения с упоминанием.
 *
 * Композер держит черновик не как строку, а как список сегментов: обычный
 * текст и «пилюли» (упоминания). Только так можно штатно собрать и `body`
 * (читаемый текст), и `formatted_body` (HTML со ссылкой), не играя в хрупкую
 * подстановку имён строкой.
 *
 * `@room` — особый случай: `userId === null`.
 */
export type TextMark =
  | "bold"
  | "italic"
  | "underline"
  | "strikethrough"
  | { color: "red" | "orange" | "green" | "blue" | "purple" };

export type RichSegment =
  | { type: "text"; text: string; marks?: TextMark[] }
  | { type: "mention"; userId: string | null; displayName: string };

/**
 * Собрать из сегментов три части content события, которые обязаны совпадать
 * между собой, иначе получатель не получит ни push, ни подсветку:
 *   1. `body` — читаемый текст с именем (для старых клиентов без HTML);
 *   2. `formatted_body` + `format` — HTML со ссылкой на matrix.to;
 *   3. `m.mentions` — MSC3952, источник истины для push-правил сервера.
 *
 * Вынесено из sendTextMessage, потому что та же тройка нужна правке
 * (m.new_content) и реплаю: писать её трижды руками — верный способ
 * однажды забыть одну из частей (именно так и случилось с editMessage
 * при слиянии этапов 3-5 — потерянные упоминания, патч Антона 10:24).
 *
 * @returns null, если нет ни упоминаний, ни форматирования: тогда content
 *          собирается обычным plain-text путём, без HTML и без m.mentions.
 */
function buildRichParts(
  roomId: string,
  segments: RichSegment[],
): { body: string; formattedBody: string; mentions: { user_ids?: string[]; room: boolean } } | null {
  const mentions = segments.filter(
    (s): s is Extract<RichSegment, { type: "mention" }> => s.type === "mention",
  );
  const hasFormatting = segments.some((s) => s.type === "text" && (s.marks?.length ?? 0) > 0);
  if (mentions.length === 0 && !hasFormatting) return null;

  const userIds = mentions
    .filter((m) => m.userId !== null)
    .map((m) => m.userId as string);
  const hasRoom = mentions.some((m) => m.userId === null);

  // Only tags and fixed attributes emitted below are allowed. User text is
  // escaped first, and colors are mapped from a closed palette: no arbitrary
  // HTML/CSS can reach formatted_body.
  const colorValues = {
    red: "#d64545",
    orange: "#d97706",
    green: "#16845b",
    blue: "#2878d0",
    purple: "#8056c7",
  } as const;
  const formatText = (segment: Extract<RichSegment, { type: "text" }>) => {
    let html = escapeHtml(segment.text);
    for (const mark of segment.marks ?? []) {
      if (mark === "bold") html = `<strong>${html}</strong>`;
      else if (mark === "italic") html = `<em>${html}</em>`;
      else if (mark === "underline") html = `<u>${html}</u>`;
      else if (mark === "strikethrough") html = `<del>${html}</del>`;
      else html = `<span data-mx-color="${colorValues[mark.color]}">${html}</span>`;
    }
    return html;
  };

  const body = segments.map((s) => (s.type === "text" ? s.text : s.displayName)).join("");
  const formattedBody = segments
    .map((s) => {
      if (s.type === "text") return formatText(s);
      if (s.userId === null) {
        return `<a href="https://matrix.to/#/${encodeURIComponent(roomId)}">${escapeHtml(s.displayName)}</a>`;
      }
      return `<a href="https://matrix.to/#/${encodeURIComponent(s.userId)}">${escapeHtml(s.displayName)}</a>`;
    })
    .join("");

  return {
    body,
    formattedBody,
    mentions: {
      ...(userIds.length > 0 ? { user_ids: [...new Set(userIds)] } : {}),
      room: hasRoom,
    },
  };
}

/**
 * Отправить текстовое сообщение, возможно с упоминаниями.
 *
 * Пустой список сегментов или единственный пустой текст — обычная отправка
 * без HTML: матрица совпадает с тем, что делал голый sendTextMessage раньше.
 */
export async function sendTextMessage(
  client: MatrixClient,
  roomId: string,
  segments: RichSegment[],
): Promise<void> {
  const rich = buildRichParts(roomId, segments);
  // Только текст без упоминаний → пусть SDK отправит обычное сообщение.
  if (!rich) {
    const body = segments.map((s) => (s.type === "text" ? s.text : "")).join("");
    await withRateLimit(() => client.sendTextMessage(roomId, body));
    return;
  }

  // sendMessage с ПОЛНЫМ content — а не sendTextMessage(roomId, body, html,
  // extra): так мы сами владеем каждым полем (msgtype, format, formatted_body,
  // m.mentions) и не зависим от сигнатуры-обёртки SDK, которая менялась между
  // версиями. Паттерн тот же, что в sendAttachment ниже.
  await withRateLimit(() =>
    client.sendMessage(roomId, {
      msgtype: MsgType.Text,
      body: rich.body,
      format: "org.matrix.custom.html",
      formatted_body: rich.formattedBody,
      "m.mentions": rich.mentions,
    }),
  );
}

/** Экранировать HTML — тело сообщения не должно сломать разметку пилюль. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Отправить файл (вложение).
 *
 * Два шага: загрузка в media-репозиторий (`uploadContent` -> mxc://) и
 * отправка события с этим URL. msgtype выбираем по MIME: картинка должна
 * приходить как `m.image`, иначе Element покажет её файлом-скрепкой.
 *
 * `info` заполняем тем, что реально знаем из File (размер, MIME, имя).
 * Ширину/высоту картинки НЕ придумываем: их пришлось бы измерять загрузкой
 * в <img>, а неверные размеры хуже отсутствующих.
 *
 * ЗАШИФРОВАННЫЕ комнаты сюда не попадают: crypto выключен, шифровать вложение
 * нечем — вызывающий код обязан не показывать кнопку в таких комнатах.
 */
export async function sendAttachment(
  client: MatrixClient,
  roomId: string,
  file: File,
  onProgress?: (percent: number) => void,
  caption?: string,
): Promise<void> {
  const up = await client.uploadContent(file, {
    name: file.name,
    type: file.type || "application/octet-stream",
    progressHandler: (p: { loaded: number; total: number }) => {
      if (p.total > 0) onProgress?.(Math.round((p.loaded / p.total) * 100));
    },
  });

  const mimetype = file.type || "application/octet-stream";
  const common = {
    // Matrix convention for captioned media: `body` is the visible caption,
    // while `filename` preserves the original download name. Keeping both
    // avoids turning a caption into a bogus filename in other clients.
    body: caption ?? file.name,
    filename: file.name,
    url: up.content_uri,
    info: { size: file.size, mimetype },
  };

  // Ветвим ЯВНО, а не подставляем msgtype переменной: типы содержимого сообщения
  // в SDK 42.3.0 — размеченное объединение по msgtype (ImageContent | FileContent |
  // …), и переменная-msgtype его не сужает. Одна строка на вариант дешевле, чем
  // приведение типа, которое заодно спрятало бы настоящую ошибку в поле info.
  await withRateLimit(() => {
    if (file.type.startsWith("image/")) {
      return client.sendMessage(roomId, { msgtype: MsgType.Image, ...common });
    }
    if (file.type.startsWith("video/")) {
      return client.sendMessage(roomId, { msgtype: MsgType.Video, ...common });
    }
    if (file.type.startsWith("audio/")) {
      return client.sendMessage(roomId, { msgtype: MsgType.Audio, ...common });
    }
    return client.sendMessage(roomId, { msgtype: MsgType.File, ...common });
  });
}

// --- Сообщения: редактирование, удаление, реплаи, реакции (этапы 3-5) -------

/**
 * Отправить голосовое сообщение (Антон, 2026-09-14 11:10).
 *
 * Файл уже записан Composer'ом в ogg/opus. Отправляем как m.audio с
 * org.matrix.msc1767.audio (длительность + waveform) и m.voice — иначе
 * другие клиенты покажут его обычным файлом, а не голосовым.
 */
export async function sendVoiceMessage(
  client: MatrixClient,
  roomId: string,
  file: File,
  duration: number,
  waveform: number[],
): Promise<void> {
  const up = await client.uploadContent(file, {
    name: file.name,
    type: file.type || "audio/ogg",
  });

  // MSC1767: ключи org.matrix.msc1767.audio и m.voice (неустойчивый, для
  // Element) — это валидное расширение контента голосового m.audio, которое
  // сервер и клиенты принимают, но в текущей версии типов matrix-js-sdk ещё
  // не описано. Сужаем каст до RoomMessageEventContent — ПОВЕДЕНИЕ НЕ МЕНЯЕТСЯ,
  // отправляется ровно тот же JSON.
  const voiceContent = {
    msgtype: MsgType.Audio,
    body: file.name,
    url: up.content_uri,
    info: {
      size: file.size,
      mimetype: file.type || "audio/ogg",
      duration: duration * 1000,
    },
    "org.matrix.msc1767.audio": {
      duration: duration * 1000,
      waveform,
    },
    "m.voice": {},
  } as RoomMessageEventContent;

  await withRateLimit(() => client.sendMessage(roomId, voiceContent));
}

/**
 * Редактировать своё сообщение (m.replace, MSC2676).
 *
 * ПРАВИЛА СОВМЕСТИМОСТИ:
 * 1. `body` ДОЛЖЕН начинаться с `* ` — fallback для клиентов, не знающих
 *    m.replace. Без него Element покажет правку как новое сообщение.
 * 2. `m.new_content` — полное новое содержимое (body + formatted_body +
 *    m.mentions, если есть). Его читают новые клиенты.
 * 3. `m.relates_to.rel_type = "m.replace"` + `event_id` цели — обязательны.
 *
 * УПОМИНАНИЯ В ПРАВКЕ (MSC3952, патч Антона 2026-09-10 10:24):
 * - В TOP-LEVEL content идут ТОЛЬКО НОВЫЕ упоминания (new − old): сервер
 *   пушит по top-level, и без вычитания уже упомянутые получали бы
 *   повторный push при каждой правке опечатки.
 * - В `m.new_content.m.mentions` — ПОЛНЫЙ актуальный набор: старые (из
 *   исходного события) ∪ новые (из сегментов). Известное ограничение:
 *   если правкой имя упомянутого СТЁРЛИ из текста, оно останется в
 *   new_content.mentions — композер при входе в правку не восстанавливает
 *   старые пилюли, и надёжно узнать, кто вычеркнут, мы не можем, не
 *   прибегая к сравнению имён строкой (что клиент сознательно не делает).
 *   Избыточная запись безопаснее молчаливой потери.
 * - Правка без упоминаний вообще — обычный plain-text content, как раньше.
 *
 * @param original — исходное событие (нужно, чтобы прочитать СТАРЫЕ
 *   m.mentions для вычитания; по одному eventId мы бы его не нашли).
 * @param segments — новый текст в виде сегментов (как у sendTextMessage).
 */
export async function editMessage(
  client: MatrixClient,
  roomId: string,
  original: MatrixEvent,
  segments: RichSegment[],
): Promise<void> {
  const eventId = original.getId();
  if (!eventId) throw new Error(tr("ui.net_eventid_u_redaktiruemogo_soobscheniya"));

  const rich = buildRichParts(roomId, segments);
  const fallbackBody = `* ${segments.map((s) => (s.type === "text" ? s.text : s.displayName)).join("")}`;

  // Без упоминаний — прежний простой путь.
  if (!rich) {
    await withRateLimit(() =>
      client.sendEvent(roomId, EventType.RoomMessage, {
        msgtype: MsgType.Text,
        body: fallbackBody,
        "m.new_content": {
          msgtype: MsgType.Text,
          body: segments.map((s) => (s.type === "text" ? s.text : s.displayName)).join(""),
        },
        "m.relates_to": {
          rel_type: RelationType.Replace,
          event_id: eventId,
        },
      }),
    );
    return;
  }

  // Старые упоминания — из исходного события. Если оригинал уже правили,
  // актуальный набор живёт в replacingEvent; берём его при наличии.
  const replacing = original.replacingEvent();
  const oldContent = replacing
    ? ((replacing.getContent()["m.new_content"] as Record<string, unknown> | undefined) ??
        replacing.getContent())
    : original.getContent();
  const oldMentions = (oldContent["m.mentions"] ?? {}) as {
    user_ids?: string[];
    room?: boolean;
  };
  const oldUserIds = new Set(oldMentions.user_ids ?? []);
  const oldRoom = Boolean(oldMentions.room);

  // top-level: ТОЛЬКО добавленные правкой.
  const newOnlyUserIds = (rich.mentions.user_ids ?? []).filter((id) => !oldUserIds.has(id));
  const newOnlyRoom = rich.mentions.room && !oldRoom;
  const topMentions =
    newOnlyUserIds.length > 0 || newOnlyRoom
      ? {
          ...(newOnlyUserIds.length > 0 ? { user_ids: newOnlyUserIds } : {}),
          room: newOnlyRoom,
        }
      : undefined;

  // m.new_content: полный набор = старые ∪ новые.
  const fullUserIds = [...new Set([...oldUserIds, ...(rich.mentions.user_ids ?? [])])];
  const fullRoom = oldRoom || rich.mentions.room;

  await withRateLimit(() =>
    client.sendEvent(roomId, EventType.RoomMessage, {
      msgtype: MsgType.Text,
      body: fallbackBody,
      format: "org.matrix.custom.html",
      formatted_body: `* ${rich.formattedBody}`,
      ...(topMentions ? { "m.mentions": topMentions } : {}),
      "m.new_content": {
        msgtype: MsgType.Text,
        body: rich.body,
        format: "org.matrix.custom.html",
        formatted_body: rich.formattedBody,
        "m.mentions": {
          ...(fullUserIds.length > 0 ? { user_ids: fullUserIds } : {}),
          room: fullRoom,
        },
      },
      "m.relates_to": {
        rel_type: RelationType.Replace,
        event_id: eventId,
      },
    }),
  );
}

/**
 * Удалить сообщение (redact). Необратимо.
 *
 * ОСОБЫЙ СЛУЧАЙ — локальное эхо (pending/failed): оно ещё не на сервере,
 * redact его не тронет. Вместо этого вызываем cancelPendingEvent — SDK
 * убирает его из таймлайна локально. Это НЕ то же самое, что redact:
 * сервер ничего не узнает, другие участники не увидят.
 *
 * Для уже отправленных событий вызываем client.redactEvent. Ошибку 403
 * (нет прав на чужое сообщение) не прячем — humanError переведёт её
 * в понятный текст, а UI покажет её в диалоге подтверждения.
 */
export async function redactMessage(
  client: MatrixClient,
  room: Room,
  event: MatrixEvent,
): Promise<void> {
  // Локальное эхо: ещё не ушло на сервер. Отменяем, а не redact.
  if (event.status === EventStatus.SENDING || event.status === EventStatus.QUEUED) {
    client.cancelPendingEvent(event);
    return;
  }
  // Failed тоже можно «удалить» — но cancelPendingEvent на NOT_SENT бросит
  // (по докам: только QUEUED/NOT_SENT/ENCRYPTING). Для failed redact тоже
  // валиден, сервер его примет. Но проще и надёжнее: failed — это наше
  // локальное состояние, отменяем его же, чтобы не оставлять «не отправлено»
  // в ленте как вечное напоминание.
  if (event.status === EventStatus.NOT_SENT) {
    client.cancelPendingEvent(event);
    return;
  }

  const eventId = event.getId();
  if (!eventId) throw new Error(tr("ui.net_eventid_dlya_redact"));
  await withRateLimit(() => client.redactEvent(room.roomId, eventId));
}

/**
 * Ответить на сообщение (m.in_reply_to).
 *
 * ПРАВИЛА СОВМЕСТИМОСТИ (спека, раздел «Rich replies»):
 * 1. В `body` добавляем fallback-цитату: `> <@sender:server> текст\n\nответ`.
 *    Без неё старые клиенты (Element < 1.11) покажут реплай как обычное
 *    сообщение без контекста.
 * 2. `m.relates_to.m.in_reply_to.event_id` — ссылка на оригинал. Это
 *    НЕ rel_type: реплай — свойство сообщения, а не отдельный тип связи.
 * 3. Цитируем только текст: для вложений fallback показывает имя файла.
 *
 * УПОМИНАНИЯ В ОТВЕТЕ (патч Антона 2026-09-10 10:24): упоминание в реплае
 * обязано пушить так же, как в обычном сообщении. Поэтому при наличии
 * пилюль собираем полную тройку (body/formatted_body/m.mentions) через
 * buildRichParts — как sendTextMessage. Fallback-цитату в HTML-варианте
 * оформляем <mx-reply>, чтобы клиенты не рисовали цитату дважды (спека:
 * из formatted_body текстовый fallback должен быть вырезан).
 */
export async function sendReply(
  client: MatrixClient,
  roomId: string,
  replyTo: MatrixEvent,
  segments: RichSegment[],
): Promise<void> {
  const originalSender = replyTo.getSender() ?? tr("timeline.senderUnknown");
  // Отвечаем на реплай: в его body уже лежит ЧУЖАЯ fallback-цитата. Если её
  // не срезать, наш fallback вложит цитату в цитату и с каждым ответом текст
  // будет расти. Спека требует цитировать видимый текст, а не сырой body.
  const originalBody = stripReplyFallback(
    String(replyTo.getContent().body ?? ""),
    replyTargetId(replyTo) !== null,
  );
  // Fallback-цитата: одна строка, до 100 символов, без переносов.
  const quote = originalBody.replace(/\s+/g, " ").slice(0, 100);
  const fallback = `> <${originalSender}> ${quote}\n\n`;
  const plainBody = segments.map((s) => (s.type === "text" ? s.text : s.displayName)).join("");

  const rich = buildRichParts(roomId, segments);

  await withRateLimit(() => {
    if (!rich) {
      return client.sendEvent(roomId, EventType.RoomMessage, {
        msgtype: MsgType.Text,
        body: fallback + plainBody,
        "m.relates_to": {
          "m.in_reply_to": {
            event_id: replyTo.getId() ?? "",
          },
        },
      });
    }
    // С упоминаниями: HTML-цитата по спеке + formatted тело + m.mentions.
    const originalId = replyTo.getId() ?? "";
    const htmlQuote =
      `<mx-reply><blockquote>` +
      `<a href="https://matrix.to/#/${roomId}/${originalId}">${tr("timeline.inReplyTo")}</a> ` +
      `<a href="https://matrix.to/#/${originalSender}">${escapeHtml(originalSender)}</a>` +
      `<br>${escapeHtml(quote)}` +
      `</blockquote></mx-reply>`;
    return client.sendEvent(roomId, EventType.RoomMessage, {
      msgtype: MsgType.Text,
      body: fallback + rich.body,
      format: "org.matrix.custom.html",
      formatted_body: htmlQuote + rich.formattedBody,
      "m.mentions": rich.mentions,
      "m.relates_to": {
        "m.in_reply_to": {
          event_id: originalId,
        },
      },
    });
  });
}

/**
 * Поставить реакцию (m.annotation).
 *
 * Снятие реакции — НЕ этот вызов: надо redact своего события m.reaction
 * (см. redactMessage). Здесь только добавление.
 *
 * `key` — это эмодзи или произвольная строка. Мы ограничиваемся эмодзи:
 * произвольный текст в key — это опросы/голосования, отдельная история.
 */
export async function sendReaction(
  client: MatrixClient,
  roomId: string,
  eventId: string,
  emoji: string,
): Promise<void> {
  await withRateLimit(() =>
    client.sendEvent(roomId, EventType.Reaction, {
      "m.relates_to": {
        rel_type: RelationType.Annotation,
        event_id: eventId,
        key: emoji,
      },
    }),
  );
}

// --- Треды (этап 5, MSC3440) -------------------------------------------------

/**
 * Отправить ответ в тред.
 *
 * ПОЧЕМУ НЕ ЧЕРЕЗ thread.reply(): в SDK 42.3.0 этот метод есть, но он
 * собирает content сам и не даёт вставить `m.mentions` (MSC3952). А упоминание
 * в треде обязано пушить так же, как в комнате (патч Антона 10:24). Поэтому
 * собираем content руками, как sendReply, но с rel_type="m.thread".
 *
 * ВАЖНО: `event_id` в relates_to — это КОРЕНЬ треда (root), а не последний
 * ответ. Именно так группирует сервер и другие клиенты.
 */
export async function sendThreadMessage(
  client: MatrixClient,
  roomId: string,
  threadRootId: string,
  segments: RichSegment[],
  replyToEventId?: string,
): Promise<void> {
  const rich = buildRichParts(roomId, segments);
  const plainBody = segments.map((s) => (s.type === "text" ? s.text : s.displayName)).join("");

  const relatesTo: Record<string, unknown> = {
    rel_type: RelationType.Thread,
    event_id: threadRootId,
  };
  if (replyToEventId) {
    relatesTo["m.in_reply_to"] = { event_id: replyToEventId };
  }

  await withRateLimit(() => {
    if (!rich) {
      return client.sendEvent(roomId, EventType.RoomMessage, {
        msgtype: MsgType.Text,
        body: plainBody,
        "m.relates_to": relatesTo,
      });
    }
    return client.sendEvent(roomId, EventType.RoomMessage, {
      msgtype: MsgType.Text,
      body: rich.body,
      format: "org.matrix.custom.html",
      formatted_body: rich.formattedBody,
      "m.mentions": rich.mentions,
      "m.relates_to": relatesTo,
    });
  });
}

// --- Настройки комнаты (этап 2) ---------------------------------------------

/**
 * Общий помощник для state events: отправляем content ЦЕЛИКОМ.
 * Для state events с «чужими» полями (power_levels) читаем текущий state,
 * меняем одно поле, отправляем обратно всё. Для name/topic/avatar/join_rules/
 * history_visibility поля свои, но единый паттерн дешевле, чем пять разных
 * функций.
 */
async function sendStateField(
  client: MatrixClient,
  room: Room,
  eventType: keyof StateEvents,
  content: Record<string, unknown>,
): Promise<void> {
  await withRateLimit(() =>
    client.sendStateEvent(room.roomId, eventType, content as never),
  );
}

/** Переименовать комнату. Пустое имя = удалить имя (комната станет безымянной). */
export async function setRoomName(
  client: MatrixClient,
  room: Room,
  name: string,
): Promise<void> {
  const current = room.currentState
    .getStateEvents(EventType.RoomName, "")
    ?.getContent() ?? {};
  await sendStateField(client, room, EventType.RoomName, { ...current, name });
}

/** Изменить topic. Пустой topic = удалить. */
export async function setRoomTopic(
  client: MatrixClient,
  room: Room,
  topic: string,
): Promise<void> {
  const current = room.currentState
    .getStateEvents(EventType.RoomTopic, "")
    ?.getContent() ?? {};
  await sendStateField(client, room, EventType.RoomTopic, { ...current, topic });
}

/** Установить аватар комнаты. file — из input[type=file]; null — удалить аватар. */
export async function setRoomAvatar(
  client: MatrixClient,
  room: Room,
  file: File | null,
): Promise<void> {
  if (!file) {
    // Удаление аватара: отправляем state event с пустым url.
    const current = room.currentState
      .getStateEvents(EventType.RoomAvatar, "")
      ?.getContent() ?? {};
    await sendStateField(client, room, EventType.RoomAvatar, { ...current, url: "" });
    return;
  }
  // ТОЛЬКО JPEG — та же дверь, что у аватара профиля (см. avatarImage.ts:
  // PNG ломал картинку в push на iOS, замерено 26.09). Стоит ДО загрузки:
  // не-JPEG не попадает ни в media-репозиторий, ни в state-event.
  const jpeg = await ensureJpegAvatar(file);
  const up = await client.uploadContent(jpeg, {
    name: jpeg.name,
    type: "image/jpeg",
  });
  const current = room.currentState
    .getStateEvents(EventType.RoomAvatar, "")
    ?.getContent() ?? {};
  await sendStateField(client, room, EventType.RoomAvatar, { ...current, url: up.content_uri });
}

/**
 * Изменить join rules. Допустимые значения по спеке: "public" | "knock" | "invite"
 * | "private" (restricted — отдельная история, не здесь). Мы даём только
 * "public" и "invite" (приватная), knock — за фичефлагом, см. план I.2.5.
 */
export async function setJoinRules(
  client: MatrixClient,
  room: Room,
  joinRule: "public" | "invite",
): Promise<void> {
  const current = room.currentState
    .getStateEvents("m.room.join_rules", "")
    ?.getContent() ?? {};
  await sendStateField(client, room, EventType.RoomJoinRules, { ...current, join_rule: joinRule });
}

/**
 * Изменить history visibility. Допустимые значения: "invited" | "joined" |
 * "shared" | "world_readable". Мы даём "shared" (видно с момента входа)
 * и "joined" (видно только с момента входа) — самые ходовые.
 */
export async function setHistoryVisibility(
  client: MatrixClient,
  room: Room,
  visibility: "shared" | "joined",
): Promise<void> {
  const current = room.currentState
    .getStateEvents("m.room.history_visibility", "")
    ?.getContent() ?? {};
  await sendStateField(client, room, EventType.RoomHistoryVisibility, {
    ...current,
    history_visibility: visibility,
  });
}
